import {
  CaretLeftIcon,
  CaretRightIcon,
  CheckIcon,
  HouseIcon,
  LockSimpleIcon,
  PlusIcon,
  SquaresFourIcon,
} from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";

import {
  getBuilderSteps,
  isCustomBuilderStep,
  type BuilderView,
} from "@/client/constants/builder-steps";
import { useStepFormResume } from "@/client/providers/step-form";
import { useResumeStore } from "@/client/stores/resume";

import { AddSectionDialog } from "./add-section-dialog";
import { getSidebarItem } from "./sidebar-config";
import { isStepComplete } from "./step-status";

type Props = {
  collapsed: boolean;
  onCollapse: () => void;
  onNavigate?: () => void;
};

export const BuilderSidebar = ({ collapsed, onCollapse, onNavigate }: Props) => {
  const navigate = useNavigate();

  const { selector, setSelector } = useStepFormResume();

  const resume = useResumeStore((state) => state.resume);

  const [dialogOpen, setDialogOpen] = useState(false);

  const steps = useMemo(
    () => getBuilderSteps(resume.data.metadata.layout, resume.data.sections),
    [resume.data.metadata.layout, resume.data.sections],
  );

  const completed = useMemo(
    () => steps.map((step) => isStepComplete(step, resume)),
    [resume, steps],
  );

  const firstIncomplete = completed.findIndex(
    (value, index) => !value && steps[index] !== "finalization",
  );

  const select = (step: BuilderView) => {
    setSelector(step);
    onNavigate?.();
  };

  return (
    <aside
      className={cn(
        "relative flex h-full shrink-0 flex-col",
        "bg-gradient-to-b",
        "from-[#4652B8] via-[#5F6DDD] to-[#7482EB]",
        "text-white",
        "shadow-[12px_0_38px_-20px_rgb(70_82_184/0.65)]",
        "transition-[width] duration-200",
        collapsed ? "w-[72px]" : "w-[232px]",
      )}
    >
      {/* Bouton de réduction */}
      <button
        type="button"
        onClick={onCollapse}
        aria-label={collapsed ? "Déployer la navigation" : "Réduire la navigation"}
        className={cn(
          "absolute -right-4 top-6 z-20",
          "grid size-8 place-items-center",
          "rounded-lg",
          "bg-gradient-to-br",
          "from-[#4F8FEA] to-[#70B7F2]",
          "text-white",
          "shadow-[0_10px_25px_rgb(79_143_234/0.35)]",
          "ring-1 ring-white/25",
          "transition-all duration-200",
          "hover:scale-105 hover:brightness-105",
          "active:scale-95",
        )}
      >
        {collapsed ? (
          <CaretRightIcon size={16} weight="bold" />
        ) : (
          <CaretLeftIcon size={16} weight="bold" />
        )}
      </button>

      {/* Logo */}
      <div className="flex h-[84px] shrink-0 items-center justify-center border-b border-white/10 px-3">
        <img
          src="/logo/mon-cv-pro_logo.svg"
          alt="Mon CV Pro"
          className={cn("w-auto object-contain", collapsed ? "h-9" : "h-14")}
        />
      </div>

      {/* Navigation */}
      <nav
        className={cn(
          "min-h-0 flex-1",
          "overflow-y-auto overflow-x-hidden",
          "px-2.5 pb-3",
          "[scrollbar-color:#CFD5FF_transparent]",
          "[scrollbar-width:thin]",
        )}
      >
        {/* Tableau de bord */}
        <button
          type="button"
          onClick={() => {
            void navigate("/client/user-space/resumes");
          }}
          className={cn(
            "mb-1 flex h-10 w-full",
            "items-center gap-3 rounded-lg px-3",
            "text-xs font-semibold text-white/80",
            "transition-all duration-150",
            "hover:bg-white/12 hover:text-white",
            "active:scale-[0.98]",
            collapsed && "justify-center px-0",
          )}
        >
          <HouseIcon size={18} weight="duotone" />

          <span className={cn(collapsed && "hidden")}>Tableau de bord</span>
        </button>

        <div className="my-3 h-px bg-white/15" />

        {/* Étapes */}
        <div className="space-y-1">
          {steps.map((step, index) => {
            const customName = isCustomBuilderStep(step)
              ? resume.data.sections.custom?.[step.slice(7)]?.name
              : undefined;

            const { label, icon: Icon } = getSidebarItem(step, customName);

            const active = selector === step;
            const done = completed[index];

            /*
             * Une rubrique déjà remplie reste
             * toujours accessible.
             *
             * Cela permet aux données importées
             * par OCR d’être consultées et
             * modifiées, même si une rubrique
             * située avant est incomplète.
             */
            const locked =
              firstIncomplete >= 0 && index > firstIncomplete && !done && step !== "finalization";

            return (
              <button
                key={step}
                type="button"
                disabled={locked}
                onClick={() => {
                  select(step);
                }}
                className={cn(
                  "flex h-10 w-full",
                  "items-center gap-3 rounded-lg",
                  "px-3 text-left text-xs",
                  "font-semibold",
                  "transition-all duration-150",
                  "focus-visible:outline-none",
                  "focus-visible:ring-2",
                  "focus-visible:ring-[#CFD5FF]",
                  collapsed && "justify-center px-0",

                  active && [
                    "bg-white",
                    "text-[#4652B8]",
                    "shadow-[0_12px_28px_rgb(39_45_109/0.18)]",
                  ],

                  !active && !locked && ["text-white/85", "hover:bg-white/12", "hover:text-white"],

                  locked && ["cursor-not-allowed", "text-[#E9ECFF]/45"],
                )}
              >
                <span
                  className={cn(
                    "grid size-7 shrink-0",
                    "place-items-center rounded-md",
                    "transition-colors",
                    active ? "bg-[#E9ECFF]" : "bg-transparent",
                  )}
                >
                  <Icon size={18} weight={active ? "fill" : "duotone"} className="shrink-0" />
                </span>

                {!collapsed && <span className="min-w-0 flex-1 truncate">{label}</span>}

                {!collapsed && done && !active && (
                  <CheckIcon
                    size={15}
                    weight="bold"
                    className={cn(
                      "shrink-0 rounded-full",
                      "bg-emerald-400 p-0.5",
                      "text-white",
                      "shadow-[0_4px_12px_rgb(52_211_153/0.3)]",
                    )}
                  />
                )}

                {!collapsed && locked && (
                  <LockSimpleIcon size={14} weight="fill" className="text-[#E9ECFF]/55" />
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Actions du bas */}
      <div
        className={cn(
          "shrink-0",
          "border-t border-white/15",
          "bg-gradient-to-b",
          "from-[#5F6DDD]/35",
          "to-[#4652B8]/45",
          "px-2.5 py-3",
          "backdrop-blur-sm",
        )}
      >
        {/* Ajouter une section */}
        <button
          type="button"
          onClick={() => {
            setDialogOpen(true);
          }}
          className={cn(
            "flex h-10 w-full",
            "items-center gap-3 rounded-lg",
            "border border-[#C8F0E4]/80",
            "px-3 text-xs font-semibold",
            "text-white",
            "transition-all duration-150",
            "hover:border-[#E8FAF4]",
            "hover:bg-[#E8FAF4]/12",
            "active:scale-[0.98]",
            collapsed && "justify-center px-0",
          )}
        >
          <PlusIcon size={17} weight="bold" className="text-[#C8F0E4]" />

          {!collapsed && <span>Ajouter une section</span>}
        </button>

        {/* Modèles */}
        <button
          type="button"
          onClick={() => {
            select("templates");
          }}
          className={cn(
            "mt-2 flex h-11 w-full",
            "items-center gap-3 rounded-lg",
            "px-3 text-xs font-bold",
            "transition-all duration-150",
            "active:scale-[0.98]",

            selector === "templates"
              ? ["bg-white", "text-[#4652B8]", "shadow-[0_12px_28px_rgb(39_45_109/0.18)]"]
              : ["bg-white/10", "text-white", "hover:bg-white/15"],

            collapsed && "justify-center px-0",
          )}
        >
          <span
            className={cn(
              "grid size-7 shrink-0",
              "place-items-center rounded-md",
              selector === "templates" ? "bg-[#E9ECFF]" : "bg-white/5",
            )}
          >
            <SquaresFourIcon size={18} weight="duotone" />
          </span>

          {!collapsed && <span>Modèles</span>}
        </button>
      </div>

      <AddSectionDialog open={dialogOpen} onOpenChange={setDialogOpen} onCreated={select} />
    </aside>
  );
};
